import React from 'react';
import type { ActionButtonVariant } from '../action-button/ActionButton';

export type UndoActionButtonData = {
  label?: string;
  undoLabel?: string;
  id?: string;
  dataTestId?: string;
  variant?: ActionButtonVariant;
  ariaLabel?: string;
  // Must match the `.tab-membership button .red-slider.animate` CSS
  // transition duration in `app/styles/pages/modal-profile.scss` so the
  // slider visually finishes at the same moment `onCommit` fires.
  durationMs?: number;
  onCommit?: () => void;
  onCancel?: () => void;
};

type Props = {
  data: UndoActionButtonData;
};

const DEFAULT_DURATION_MS = 3000;

export function UndoActionButton({ data }: Props) {
  const [isUndoing, setIsUndoing] = React.useState(false);
  // `animateSlider` lags one animation frame behind `isUndoing` so the
  // slider paints at `width: 0` before the `.animate` class is added;
  // otherwise the CSS width transition has no "before" state and the
  // bar snaps to full width instantly.
  const [animateSlider, setAnimateSlider] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = React.useRef<number | null>(null);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cancelRaf = React.useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (isUndoing) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setAnimateSlider(true);
      });
      return () => {
        cancelRaf();
      };
    }
    setAnimateSlider(false);
    return undefined;
  }, [isUndoing, cancelRaf]);

  React.useEffect(() => {
    return () => {
      clearTimer();
      cancelRaf();
    };
  }, [clearTimer, cancelRaf]);

  if (!data) {
    return null;
  }

  const {
    label,
    undoLabel,
    id,
    dataTestId,
    variant = 'primary',
    ariaLabel,
    durationMs = DEFAULT_DURATION_MS,
    onCommit,
    onCancel,
  } = data;

  const isDisabled = variant === 'disabled';

  const handleClick = () => {
    if (isDisabled) {
      return;
    }
    if (isUndoing) {
      clearTimer();
      setIsUndoing(false);
      if (onCancel) {
        onCancel();
      }
      return;
    }
    setIsUndoing(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setIsUndoing(false);
      if (onCommit) {
        onCommit();
      }
    }, durationMs);
  };

  const sliderClassName = animateSlider ? 'red-slider animate' : 'red-slider';

  return (
    <button
      type="button"
      id={id || undefined}
      data-testid={dataTestId || undefined}
      aria-label={ariaLabel || undefined}
      aria-disabled={isDisabled || undefined}
      disabled={isDisabled}
      onClick={handleClick}
    >
      <div hidden={isUndoing}>{label}</div>
      <div hidden={!isUndoing}>
        <div className="text">{undoLabel}</div>
        <div className={sliderClassName} />
      </div>
    </button>
  );
}
